if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.ifMediaStack = this.hasAttribute("data-media-stack");
        this.lastVariantMediaId = parseInt(
          this.querySelector(".is-current-variant-media").dataset.mediaId,
        ); // Current selected variant media ID

        if (!this.thumbnails) return;

        this.boundOnThumbnailClick = this.onThumbnailClick.bind(this);
        this.boundOnThumbnailKeydown = (event) => {
          if (event.code === "Enter" || event.code === "Space") {
            event.preventDefault(); // Prevent Space default scroll
            this.boundOnThumbnailClick(event);
          }
        };

        this.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((thumbnail) => {
            thumbnail.addEventListener("click", this.boundOnThumbnailClick);

            // Keyboard support: Enter and Space
            thumbnail.addEventListener("keydown", this.boundOnThumbnailKeydown);
          });

        this.boundOnSlideChanged = this.onSlideChanged.bind(this);
        this.addEventListener("sliderChanged", this.boundOnSlideChanged);
      }

      disconnectedCallback() {
        if (this.thumbnails) {
          this.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((thumbnail) => {
              if (this.boundOnThumbnailClick) {
                thumbnail.removeEventListener(
                  "click",
                  this.boundOnThumbnailClick,
                );
              }

              // Remove keyboard event
              if (this.boundOnThumbnailKeydown) {
                thumbnail.removeEventListener(
                  "keydown",
                  this.boundOnThumbnailKeydown,
                );
              }
            });
        }

        if (this.boundOnSlideChanged) {
          this.removeEventListener("sliderChanged", this.boundOnSlideChanged);
        }
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      /**
       * Handle slide changed event
       * @param event
       */
      onSlideChanged(event) {
        if (!event || !event.detail) return;

        const mediaId = event.detail.currentElement.dataset.mediaId;

        // Scroll thumbnail to current media
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return;

        if (
          !thumbnail
            .querySelector(".thumbnail-media")
            .hasAttribute("aria-current")
        ) {
          this.setActiveThumbnail(thumbnail);
        }
        if (this.thumbnails.initSliderStatus)
          this.thumbnails.moveElementToOptimalPosition(thumbnail); // Move thumbnail to optimal position
      }

      /**
       * Handle thumbnail click
       */
      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        this.setActiveThumbnail(thumbnail);

        this.changeMedia(thumbnail.getAttribute("data-target"));
      }

      /**
       * Update gallery when variant changes. Reinstalls slider if hide-variants is enabled.
       * @param currentVariantMediaId - Featured media ID of the current variant
       * @param {boolean} ifAutoScrollToTop - Whether to auto-scroll to top
       */
      updateGallery(currentVariantMediaId, ifAutoScrollToTop = false) {
        if (
          currentVariantMediaId === this.lastVariantMediaId ||
          currentVariantMediaId == null
        )
          return;
        this.lastVariantMediaId = currentVariantMediaId;

        // Update current variant media
        this.viewer
          .querySelectorAll(".is-current-variant-media")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media"),
          );
        this.viewer
          .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
          .forEach((element) =>
            element.classList.add("is-current-variant-media"),
          );

        if (this.thumbnails) {
          this.thumbnails
            .querySelector(".is-current-variant-thumbnail")
            ?.classList.remove("is-current-variant-thumbnail");
          this.thumbnails
            .querySelector(`[data-target="${currentVariantMediaId}"]`)
            ?.classList.add("is-current-variant-thumbnail");
        }

        // If hide-variants is enabled
        if (this.hasAttribute("data-hide-variants")) {
          // Switch to current variant media group
          const groupName = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          )?.dataset.mediaGroup;
          this.viewer
            .querySelectorAll(".is-current-variant-media-group")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media-group"),
            );
          if (groupName) {
            this.viewer
              .querySelectorAll(`[data-media-group='${groupName}']`)
              .forEach((element) => {
                element.classList.add("is-current-variant-media-group");
              });
          }

          if (this.thumbnails) {
            this.thumbnails
              .querySelectorAll(".is-current-variant-thumbnail-group")
              .forEach((element) =>
                element.classList.remove("is-current-variant-thumbnail-group"),
              );
            if (groupName) {
              this.thumbnails
                .querySelectorAll(`[data-thumbnail-group='${groupName}']`)
                .forEach((element) => {
                  element.classList.add("is-current-variant-thumbnail-group");
                });
            }
          }

          // Reinstall slider when hide-variants changes visible items
          if (!this.ifMediaStack || webvista.isMobileScreen()) {
            this.viewer.reInstall();
            if (this.thumbnails) this.thumbnails.reInstall();
          }
        }

        this.changeMedia(currentVariantMediaId);

        // On mobile, scroll back to top when media changes
        if (webvista.isMobileScreen() && ifAutoScrollToTop) {
          webvista.scrollElementToHeaderBottom(this, 16);
        }
      }

      /**
       * Change active media
       * @param mediaId
       */
      changeMedia(mediaId) {
        const activeMedia = this.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.ifMediaStack) {
          // Stack layout: prepend current variant featured media to top
          activeMedia.parentElement.prepend(activeMedia);
        } else {
          // Slider mode: slide to active media
          if (this.viewer.initSliderStatus)
            this.viewer.slideByElement(activeMedia);
        }
      }

      /**
       * Set active thumbnail
       * @param thumbnail
       */
      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail
          .querySelector(".thumbnail-media")
          .setAttribute("aria-current", true);
      }
    },
  );
}
